import React from "react"
import {useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import {TReachMeta} from "../../../queries/reachEngine"
import {ACampaignsCampaignTypeEnum} from "../../../services/types.generated"
import {enumTranslKey} from "../../../utils/i18n"
import {DashboardCard} from "./DashboardCard"

const FLOW_TYPES = [ACampaignsCampaignTypeEnum.NewBiz, ACampaignsCampaignTypeEnum.Upsell] as const

type TFlowType = (typeof FLOW_TYPES)[number]
type TFlowTypeUnit = keyof TReachMeta["flow_types"]

const FLOW_TYPE_UNITS: readonly TFlowTypeUnit[] = ["flows", "leads"]

const BAR_COLORS: Record<TFlowType, string> = {
  [ACampaignsCampaignTypeEnum.NewBiz]: "bg-cr-blue",
  [ACampaignsCampaignTypeEnum.Upsell]: "bg-cr-blue-mid",
}

export const FlowTypeCard: React.FC<{flowTypes: TReachMeta["flow_types"]; className?: string}> = ({
  flowTypes,
  className,
}) => {
  const {t} = useTranslation()
  const [unit, setUnit] = React.useState<TFlowTypeUnit>("flows")

  const unitLabels: Record<TFlowTypeUnit, string> = {
    flows: t("ReachEngine_FlowTypes_Flows"),
    leads: t("ReachEngine_FlowTypes_Leads"),
  }

  const counts = flowTypes[unit]
  const max = Math.max(...FLOW_TYPES.map(type => counts[type]), 0)

  const toggle = (
    <div className={"flex rounded-[8px] bg-cr-slate-4 p-[3px]"}>
      {FLOW_TYPE_UNITS.map(option => {
        const isActive = unit === option
        return (
          <button
            key={option}
            type={"button"}
            aria-pressed={isActive}
            onClick={() => setUnit(option)}
            className={twMerge(
              "rounded-[6px] px-[12px] py-[5px] text-[11.5px] font-semibold transition-colors",
              isActive ? "bg-white text-cr-slate-90" : "text-cr-slate-60 hover:text-cr-slate-90"
            )}
          >
            {unitLabels[option]}
          </button>
        )
      })}
    </div>
  )

  return (
    <DashboardCard title={t("ReachEngine_FlowTypes_Title")} action={toggle} className={className}>
      <ul role={"list"} className={"my-auto flex flex-col gap-[12px] pt-[16px]"}>
        {FLOW_TYPES.map(type => {
          const value = counts[type]
          return (
            <li key={type} data-testid={`flow-type-${type}`} className={"flex items-center gap-[10px] text-[12px]"}>
              <span className={"w-[100px] shrink-0 text-cr-slate-60"}>
                {t(enumTranslKey("ACampaignsCampaignTypeEnum", type))}
              </span>
              <div aria-hidden className={"h-[10px] min-w-0 flex-1 overflow-hidden rounded-full bg-cr-slate-8"}>
                <div
                  data-bar={type}
                  className={twMerge("h-full rounded-full", BAR_COLORS[type])}
                  style={{width: `${max > 0 ? (value / max) * 100 : 0}%`}}
                />
              </div>
              <span
                className={"min-w-12 shrink-0 text-right font-bold whitespace-nowrap text-cr-slate-90 tabular-nums"}
              >
                {value.toLocaleString()}
              </span>
            </li>
          )
        })}
      </ul>
    </DashboardCard>
  )
}
